import DOMPurify from "dompurify";

// Client-side allowlist for AuthorIt article HTML, the same lists as
// ArticleHtmlSanitizer.cs on the server. The server sanitizes what is stored;
// this pass runs in the browser that renders the result, so the final parse and
// the sanitizing parse are the same engine and a parser difference (mutation
// XSS) cannot slip through. Focus uses DOMPurify the same way for mail bodies.

// Every element found in the stored articles plus inert HTML text and table
// elements. afas_aitdata is the AuthorIt wrapper; h7 is not HTML but AuthorIt
// emits it and the stylesheet styles it.
export const ALLOWED_TAGS = [
  "a", "abbr", "afas_aitdata", "area", "b", "blockquote", "br", "caption", "cite", "code",
  "col", "colgroup", "dd", "del", "div", "dl", "dt", "em", "font",
  "h1", "h2", "h3", "h4", "h5", "h6", "h7", "hr", "i", "iframe", "img", "ins", "kbd",
  "li", "map", "mark", "ol", "p", "pre", "q", "s", "small", "source", "span", "strike",
  "strong", "sub", "sup", "table", "tbody", "td", "tfoot", "th", "thead", "tr", "u", "ul",
  "video", "wbr",
];

// Attributes allowed on every element: presentation, table layout and the ids
// and classes the stylesheet and the expanding blocks rely on.
export const GLOBAL_ATTRIBUTES = [
  "class", "id", "title", "style", "lang", "dir", "align", "valign", "width", "height",
  "border", "bgcolor", "cellpadding", "cellspacing", "colspan", "rowspan", "hspace",
  "vspace", "nowrap", "scope", "headers", "summary", "start", "value", "type", "alt",
];

// Attributes allowed on one element only. "name" stays off img and iframe,
// where it would create a window property (DOM clobbering).
export const ELEMENT_ATTRIBUTES: Readonly<Record<string, readonly string[]>> = {
  a: ["href", "name", "target", "rel"],
  area: ["href", "shape", "coords", "target", "rel"],
  img: ["src", "usemap", "loading"],
  map: ["name"],
  iframe: ["src", "frameborder", "allowfullscreen", "allow", "loading", "referrerpolicy"],
  video: ["autoplay", "loop", "muted", "playsinline", "controls", "poster", "preload"],
  source: ["src"],
  font: ["color", "size", "face"],
  ol: ["reversed"],
  q: ["cite"],
  blockquote: ["cite"],
};

// http, https, mailto, tel, or no scheme at all. DOMPurify separately allows
// data: images on img through its DATA_URI_TAGS default.
const ALLOWED_URI_REGEXP =
  /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i;

// Inline style properties that only affect layout and looks, including the
// longhands a CSS object model expands a shorthand into. No positioning, so an
// article cannot overlay the page chrome.
export const ALLOWED_STYLE_PROPERTIES = [
  "background", "background-attachment", "background-clip", "background-color",
  "background-image", "background-origin", "background-position", "background-position-x",
  "background-position-y", "background-repeat", "background-repeat-x", "background-repeat-y",
  "background-size", "border", "border-bottom", "border-bottom-color",
  "border-bottom-left-radius", "border-bottom-right-radius", "border-bottom-style",
  "border-bottom-width", "border-collapse", "border-color", "border-left", "border-left-color",
  "border-left-style", "border-left-width", "border-radius", "border-right",
  "border-right-color", "border-right-style", "border-right-width", "border-spacing",
  "border-style", "border-top", "border-top-color", "border-top-left-radius",
  "border-top-right-radius", "border-top-style", "border-top-width", "border-width",
  "caption-side", "clear", "color", "display", "empty-cells", "float", "font",
  "font-family", "font-size", "font-stretch", "font-style", "font-variant",
  "font-weight", "height", "letter-spacing", "line-height", "list-style",
  "list-style-image", "list-style-position", "list-style-type", "margin",
  "margin-bottom", "margin-left", "margin-right", "margin-top", "max-height",
  "max-width", "min-height", "min-width", "opacity", "overflow", "overflow-wrap",
  "overflow-x", "overflow-y", "padding", "padding-bottom", "padding-left",
  "padding-right", "padding-top", "table-layout", "text-align", "text-decoration",
  "text-decoration-color", "text-decoration-line", "text-decoration-style",
  "text-indent", "text-transform", "vertical-align", "visibility", "white-space",
  "width", "word-break", "word-wrap",
];

const DANGEROUS_STYLE_VALUE =
  /expression\(|javascript:|vbscript:|-moz-binding|behavior:|@import|\\/i;
const CSS_URL = /url\(\s*['"]?([^'")]*)/gi;

function isAllowedStyleDeclaration(declaration: string): boolean {
  const colon = declaration.indexOf(":");
  if (colon <= 0) return false;
  const property = declaration.slice(0, colon).trim().toLowerCase();
  const value = declaration.slice(colon + 1);
  if (!ALLOWED_STYLE_PROPERTIES.includes(property)) return false;
  if (DANGEROUS_STYLE_VALUE.test(value)) return false;
  for (const match of value.matchAll(CSS_URL)) {
    const url = (match[1] ?? "").trim();
    if (!ALLOWED_URI_REGEXP.test(url) || /^data:/i.test(url)) return false;
  }
  return true;
}

export function sanitizeStyle(style: string): string {
  return style
    .replace(/[\t\n\r]/g, "")
    .split(";")
    .map((d) => d.trim())
    .filter((d) => d.length > 0 && isAllowedStyleDeclaration(d))
    .join("; ");
}

const ALLOWED_ATTR = [
  ...GLOBAL_ATTRIBUTES,
  ...new Set(Object.values(ELEMENT_ATTRIBUTES).flat()),
];

const ALLOWED_IFRAME_HOSTS = [
  "youtube.com",
  "www.youtube.com",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
];

export function isAllowedIframeSrc(src: string | null): boolean {
  if (!src) return false;
  try {
    const url = new URL(src.trim());
    return (
      (url.protocol === "https:" || url.protocol === "http:") &&
      ALLOWED_IFRAME_HOSTS.includes(url.hostname.toLowerCase())
    );
  } catch {
    return false;
  }
}

function isAllowedAttribute(tag: string, name: string): boolean {
  return (
    GLOBAL_ATTRIBUTES.includes(name) ||
    (ELEMENT_ATTRIBUTES[tag]?.includes(name) ?? false)
  );
}

type Purifier = typeof DOMPurify;

const ELEMENT_NODE = 1;

function installHooks(purifier: Purifier): void {
  purifier.addHook("uponSanitizeElement", (node, data) => {
    if (data.tagName !== "iframe" || node.nodeType !== ELEMENT_NODE) return;
    const frame = node as Element;
    if (!isAllowedIframeSrc(frame.getAttribute("src"))) {
      frame.parentNode?.removeChild(frame);
    }
  });

  purifier.addHook("afterSanitizeAttributes", (node) => {
    if (node.nodeType !== ELEMENT_NODE) return;
    const element = node as Element;
    const tag = element.localName;
    // DOMPurify allows attributes globally; the article allowlist is per element.
    // "is" is left to DOMPurify, which voids it rather than removing it because
    // a customized built-in keeps its definition either way; an empty "is"
    // upgrades nothing, and removing it here makes some DOMs restore the value.
    for (const name of Array.from(element.getAttributeNames())) {
      if (name !== "is" && !isAllowedAttribute(tag, name)) {
        element.removeAttribute(name);
      }
    }
    const style = element.getAttribute("style");
    if (style !== null) {
      const kept = sanitizeStyle(style);
      if (kept) element.setAttribute("style", kept);
      else element.removeAttribute("style");
    }
    if (tag === "a" && element.getAttribute("target")?.toLowerCase() === "_blank") {
      // A new tab must not get a handle on this window.
      element.setAttribute("rel", "noopener noreferrer");
    }
  });
}

/**
 * Builds a sanitizer on a DOMPurify instance (the page's by default; tests pass
 * one bound to a jsdom window). Unknown elements are unwrapped so their text
 * stays; script, style, template, svg and the other elements whose content a
 * browser treats specially are removed with their content (DOMPurify's
 * FORBID_CONTENTS default), and comments go. Fails closed: without a usable DOM
 * the result is empty rather than the input.
 */
export function createArticleSanitizer(
  purifier: Purifier = DOMPurify,
): (html: string) => string {
  let hooksInstalled = false;
  return (html: string): string => {
    if (!html) return "";
    if (!purifier.isSupported) return "";
    if (!hooksInstalled) {
      installHooks(purifier);
      hooksInstalled = true;
    }
    return purifier.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ALLOWED_URI_REGEXP,
      KEEP_CONTENT: true,
    });
  };
}

export const sanitizeArticleHtml = createArticleSanitizer();
